import { find, get, startCase } from 'lodash';

import ActivityLogResultsConsts from 'client/Insights/ActivityLog/constants/ActivityLogResultsConsts';
import AssociationEventConsts, {
  resourceTypes,
  resourceValues,
} from 'client/Insights/ActivityLog/constants/AssociationEventConsts';
import Constants from 'client/util/Constants';
import EventTypes from 'client/Insights/ActivityLog/constants/EventTypes';
import arrowIcon from 'img/activityLogSummary/arrow.svg';
import deviceUpgraded from 'img/activityLogSummary/device-upgraded.svg';
import devicesIcon from 'img/activityLogSummary/devices.svg';
import ldapIcon from 'img/activityLogSummary/ldap.svg';
import loginFailIcon from 'img/activityLogSummary/login-fail.svg';
import loginSuccessIcon from 'img/activityLogSummary/login-success.svg';
import mdmIcon from 'img/activityLogSummary/mdm.svg';
import moment from 'moment';
import passwordManagerIcon from 'img/activityLogSummary/password-manager.svg';
import portalIcon from 'img/activityLogSummary/portal.svg';
import radiusIcon from 'img/activityLogSummary/radius.svg';
import recordUpdate from 'img/activityLogSummary/record-update.svg';
import singleDevice from 'img/activityLogSummary/single-device.svg';
import softwareIcon from 'img/activityLogSummary/software.svg';
import ssoIcon from 'img/activityLogSummary/sso.svg';
import systemIcon from 'img/activityLogSummary/system.svg';
import upgraded from 'img/activityLogSummary/upgraded.svg';
import userIcon from 'img/activityLogSummary/user.svg';

const { dataNotAvailable, dateTimeSecondsFormat } = Constants;
const { getResultFromRecord } = ActivityLogResultsConsts;
const { resourceToGenericTextMap, resourceToTextMap } = AssociationEventConsts;

export const eventServices = {
  directory: 'directory',
  ldap: 'ldap',
  mdm: 'mdm',
  password_manager: 'password_manager',
  radius: 'radius',
  software: 'software',
  sso: 'sso',
  systems: 'systems',
};

/**
 * This is a list of all event fields displayed in the UI.
 * The key should be used when referencing the field.
 * The value is the field name that should be displayed to customers.
 */
export const eventFields = {
  applicationId: 'Application ID',
  applicationName: 'Application Name',
  applicationDisplayName: 'Display Name',
  applicationSsoUrl: 'Application SSO URL',
  applicationSsoType: 'Application SSO Type',
  associationChange: 'Association Change',
  associationChangeTo: 'Association Change To',
  associationFromId: 'Association From ID',
  associationFromName: 'Association From Name',
  associationFromType: 'Association From Type',
  associationOperation: 'Association Operation',
  associationToId: 'Association To ID',
  associationToName: 'Association To Name',
  associationToType: 'Association To Type',
  attributes: 'Attributes',
  authIdp: 'Radius Authentication Provider',
  authMechanism: 'Authentication Mechanism',
  authMethod: 'Authentication Method',
  authType: 'Authentication Type',
  base: 'Base',
  clientIp: 'Client IP',
  commandId: 'Command ID',
  commandRequestType: 'Command Request Type',
  connectionId: 'Connection ID',
  correlationId: 'Correlation ID',
  correlationType: 'Correlation Type',
  deref: 'Deref',
  deviceGroup: 'Device Group',
  deviceId: 'Device ID',
  dn: 'Distinguished Name',
  eapType: 'EAP Type',
  errorChainDescription: 'Error Chain Description',
  errorChainErrorCode: 'Error Chain Error Code',
  errorChainErrorDomain: 'Error Chain Error Domain',
  errorCode: 'Error Code',
  errorMessage: 'Error Message',
  eventId: 'Event ID',
  eventType: 'Event Type',
  fieldsChanged: 'Fields Changed',
  filter: 'Filter',
  geoip: 'GeoIP',
  geoipContinentCode: 'GeoIP Continent Code',
  geoipCountryCode: 'GeoIP Country Code',
  geoipRegionCode: 'GeoIP Region Code',
  geoipRegionName: 'GeoIP Region Name',
  geoipTimezone: 'GeoIP Timezone',
  geoipSource: 'GeoIP (Source)',
  geoipSourceContinentCode: 'GeoIP (Source) Continent Code',
  geoipSourceCountryCode: 'GeoIP (Source) Country Code',
  geoipSourceRegionCode: 'GeoIP (Source) Region Code',
  geoipSourceRegionName: 'GeoIP (Source) Region Name',
  geoipSourceTimezone: 'GeoIP (Source) Timezone',
  idpInitiated: 'IDP Initiated',
  initiatedBy: 'Initiated By',
  initiatedByEmail: 'Initiated By Email',
  initiatedByType: 'Initiated By Type',
  initiatedByUsername: 'Initiated By Username',
  ldapObjectId: 'LDAP Object ID',
  logMessage: 'Log Message',
  mdmDeviceId: 'MDM Device ID',
  mfa: 'MFA',
  mfaState: 'MFA State',
  mfaType: 'MFA Type',
  numResults: 'Number of Results',
  operationNumber: 'Operation Number',
  organization: 'Organization',
  outerEapType: 'Outer EAP Type',
  outerErrorMessage: 'Outer Error Message',
  outerUsername: 'Outer Username',
  processName: 'Process Name',
  provider: 'Provider',
  radiusCertificateUserIdLocation: 'Certificate User ID Location',
  radiusCertificateAuthorityKeyId: 'Certificate Authority Key Id',
  radiusCertificateIssuer: 'Certificate Issuer',
  radiusCertificateSerialNumber: 'Certificate Serial No.',
  radiusPasswordEnabled: 'User Password Enabled',
  radiusUserCertsEnabled: 'User Certs Enabled',
  requestType: 'Request Type',
  resourceDisplayName: 'Resource Display Name',
  resourceDomain: 'Resource Domain',
  resourceEmail: 'Resource Email',
  resourceId: 'Resource Id',
  resourceRecipientEmail: 'Recipient Email',
  resourceEmailType: 'Recipient Email Type',
  resourceHostname: 'Resource Hostname',
  resourceName: 'Resource Name',
  resourceType: 'Resource Type',
  resourceUsername: 'Resource Username',
  result: 'Result',
  scope: 'Scope',
  searchFilter: 'Search Filter',
  service: 'Service',
  sourceIp: 'Source IP',
  startTls: 'Start TLS',
  status: 'Status',
  success: 'Success',
  system: 'Device',
  systemHostname: 'Device Hostname',
  systemOsFamily: 'Device OS',
  systemId: 'System ID',
  systemTimestamp: 'Device Timestamp',
  timestamp: 'Timestamp',
  tlsEstablished: 'TLS Established',
  user: 'User',
  userAgent: 'User Agent',
  userIdType: 'Radius User Type',
  username: 'Username',
  windowsElevated: 'Windows Elevated',
  windowsLogonType: 'Windows Logon Type',
};

export const eventServiceMetadata = {
  all: {
    defaultColumns: [
      eventFields.timestamp,
      eventFields.eventType,
      eventFields.result,
      eventFields.initiatedBy,
      eventFields.success,
      eventFields.geoip,
    ],
    icons: {
      arrow: arrowIcon,
      center: systemIcon,
      left: userIcon,
      right: portalIcon,
    },
  },
  [eventServices.directory]: {
    defaultColumns: [
      eventFields.timestamp,
      eventFields.eventType,
      eventFields.result,
      eventFields.initiatedBy,
      eventFields.success,
      eventFields.geoip,
    ],
    icons: {
      arrow: arrowIcon,
      center: systemIcon,
      left: userIcon,
      right: portalIcon,
    },
  },
  [eventServices.ldap]: {
    defaultColumns: [
      eventFields.timestamp,
      eventFields.eventType,
      eventFields.result,
      eventFields.initiatedBy,
      eventFields.authMethod,
      eventFields.geoip,
    ],
    icons: {
      arrow: arrowIcon,
      center: ldapIcon,
      left: userIcon,
      right: portalIcon,
    },
  },
  [eventServices.mdm]: {
    defaultColumns: [
      eventFields.timestamp,
      eventFields.eventType,
      eventFields.result,
      eventFields.requestType,
      eventFields.status,
      eventFields.geoip,
    ],
    icons: {
      arrow: arrowIcon,
      center: mdmIcon,
      left: userIcon,
      right: portalIcon,
    },
  },
  [eventServices.password_manager]: {
    defaultColumns: [
      eventFields.timestamp,
      eventFields.eventType,
      eventFields.result,
      eventFields.resourceName,
      eventFields.success,
    ],
    icons: {
      arrow: arrowIcon,
      center: passwordManagerIcon,
      left: userIcon,
      right: portalIcon,
    },
  },
  [eventServices.radius]: {
    defaultColumns: [
      eventFields.timestamp,
      eventFields.eventType,
      eventFields.result,
      eventFields.initiatedBy,
      eventFields.geoip,
    ],
    icons: {
      arrow: arrowIcon,
      center: radiusIcon,
      left: userIcon,
      right: portalIcon,
    },
  },
  [eventServices.software]: {
    defaultColumns: [
      eventFields.timestamp,
      eventFields.eventType,
      eventFields.result,
      eventFields.systemHostname,
      eventFields.geoip,
    ],
    icons: {
      arrow: arrowIcon,
      center: softwareIcon,
      left: userIcon,
      right: portalIcon,
    },
  },
  [eventServices.sso]: {
    defaultColumns: [
      eventFields.timestamp,
      eventFields.eventType,
      eventFields.result,
      eventFields.initiatedBy,
      eventFields.applicationName,
      eventFields.idpInitiated,
      eventFields.geoip,
    ],
    icons: {
      arrow: arrowIcon,
      center: ssoIcon,
      left: userIcon,
      right: portalIcon,
    },
  },
  [eventServices.systems]: {
    defaultColumns: [
      eventFields.timestamp,
      eventFields.eventType,
      eventFields.result,
      eventFields.initiatedBy,
      eventFields.systemHostname,
      eventFields.geoip,
    ],
    icons: {
      arrow: arrowIcon,
      center: systemIcon,
      left: userIcon,
      right: portalIcon,
    },
  },
};

/**
 * This object contains metadata for each event field.
 * Note that some fields are dynamically generated at the end of this file.
 * @param {Function} clickable - Whether the field value should be clickable for filtering
 * @param {Function} getDataValue - A function that returns the value when given the record
 * @param {String} name - The field name displayed to customers (dynamically generated at the end)
 * @param {String} path - The path to retrieve the value, used for filtering when clickable
 * @param {String} paths - An array of objects that specify paths and filters to retrieve value
 */
const metadata = {
  [eventFields.associationChangeTo]: {
    path: 'association.connection.to.type',
  },
  [eventFields.associationChange]: {
    path: 'association_change',
  },
  [eventFields.applicationId]: {
    path: 'application.id',
  },
  [eventFields.applicationName]: {
    path: 'application.name',
  },
  [eventFields.applicationDisplayName]: {
    path: 'application.displayName',
  },
  [eventFields.applicationSsoUrl]: {
    path: 'application.sso_url',
  },
  [eventFields.applicationSsoType]: {
    path: 'application.sso_type',
  },
  [eventFields.attributes]: {
    path: 'attr',
  },
  [eventFields.associationFromId]: {
    path: 'association.connection.from.object_id',
  },
  [eventFields.associationFromName]: {
    path: 'association.connection.from.name',
  },
  [eventFields.associationFromType]: {
    path: 'association.connection.from.type',
  },
  [eventFields.associationOperation]: {
    path: 'association.op',
  },
  [eventFields.associationToId]: {
    path: 'association.connection.to.object_id',
  },
  [eventFields.associationToName]: {
    path: 'association.connection.to.name',
  },
  [eventFields.associationToType]: {
    path: 'association.connection.to.type',
  },
  [eventFields.authIdp]: {
    path: 'auth_meta.auth_idp',
  },
  [eventFields.authMechanism]: {
    path: 'mech',
  },
  [eventFields.authMethod]: {
    path: 'auth_method',
  },
  [eventFields.authType]: {
    path: 'auth_type',
  },
  [eventFields.base]: {
    path: 'base',
  },
  [eventFields.clientIp]: {
    clickable: d => Boolean(d.client_ip),
    getDataValue: d => d.client_ip,
    path: 'client_ip',
  },
  [eventFields.commandId]: {
    path: 'command_uuid',
  },
  [eventFields.commandRequestType]: {
    path: 'command.request_type',
  },
  [eventFields.connectionId]: {
    path: 'connection_id',
  },
  [eventFields.correlationId]: {
    path: 'correlation.id',
  },
  [eventFields.correlationType]: {
    path: 'correlation.type',
  },
  [eventFields.deref]: {
    path: 'deref',
  },
  [eventFields.deviceId]: {
    path: 'device_object_id',
  },
  [eventFields.deviceGroup]: {
    path: 'system_group.name',
  },
  [eventFields.dn]: {
    path: 'dn',
  },
  [eventFields.eapType]: {
    path: 'eap_type',
  },
  [eventFields.errorChainDescription]: {
    path: 'error_chain.us_english_description',
  },
  [eventFields.errorChainErrorCode]: {
    path: 'error_chain.error_code',
  },
  [eventFields.errorChainErrorDomain]: {
    path: 'error_chain.error_domain',
  },
  [eventFields.errorCode]: {
    path: 'error_code',
  },
  [eventFields.errorMessage]: {
    path: 'error_message',
  },
  [eventFields.eventId]: {
    path: 'id',
  },
  [eventFields.eventType]: {
    clickable: d => Boolean(d.event_type),
    path: 'event_type',
    getDataValue: d => d.event_type,
  },
  [eventFields.fieldsChanged]: {
    getDataValue: (d) => {
      if (!(d.changes instanceof Array)) return undefined;
      if (d.changes.length === 0) return undefined;
      if (d.event_type === EventTypes.integrationattributeExclude
        || d.event_type === EventTypes.integrationattributeInclude) {
        if (d.resource.name && d.resource.name === 'userPrincipalName') {
          const sourceChanges = d.changes.filter(c => c.field === 'userPrincipalName')[0];
          let evtChangesStr = 'userPrincipalName';
          if (sourceChanges && sourceChanges.from) evtChangesStr += `\nfrom: ${sourceChanges.from}`;
          if (sourceChanges && sourceChanges.to) evtChangesStr += `\nto: ${sourceChanges.to}`;
          return evtChangesStr;
        }
        const directionChanges = d.changes.filter(c => c.field === 'direction')[0];
        let evtChangesStr = 'direction';
        if (directionChanges && directionChanges.from) evtChangesStr += `\nfrom: ${directionChanges.from}`;
        if (directionChanges && directionChanges.to) evtChangesStr += `\nto: ${directionChanges.to}`;
        return evtChangesStr;
      }
      return d.changes.map(change => change.field).join(', ');
    },
  },
  [eventFields.filter]: {
    path: 'filter',
  },
  [eventFields.geoip]: {
    getDataValue: (d) => {
      const { geoip } = d;
      if (!geoip) return undefined;

      // The api has the possiblity of returning one of two country code json keys.
      const countryCode = geoip.country_code || geoip.country_code2;
      if (!geoip.region_name && !geoip.timezone && !countryCode) return undefined;

      let geoipValue = '';
      if (geoip.timezone && !geoip.region_name && !countryCode) {
        geoipValue = geoip.timezone;
      } else {
        if (countryCode && !geoip.region_name) {
          geoipValue = countryCode;
        } else if (geoip.region_name && !countryCode) {
          geoipValue = geoip.region_name;
        } else {
          geoipValue = `${geoip.region_name}, ${countryCode}`;
        }
        if (geoip.timezone) {
          geoipValue += `; ${geoip.timezone}`;
        }
      }
      return geoipValue;
    },
  },
  [eventFields.geoipContinentCode]: {
    path: 'geoip.continent_code',
  },
  [eventFields.geoipCountryCode]: {
    // The api has the possiblity of returning one of two country code json keys.
    getDataValue: d => d.geoip.country_code2 || d.geoip.country_code,
  },
  [eventFields.geoipRegionCode]: {
    path: 'geoip.region_code',
  },
  [eventFields.geoipRegionName]: {
    path: 'geoip.region_name',
  },
  [eventFields.geoipTimezone]: {
    path: 'geoip.timezone',
  },
  [eventFields.geoipSource]: {
    getDataValue: (d) => {
      const { src_geoip: srcGeoip } = d;
      if (!srcGeoip) return undefined;

      // The api has the possiblity of returning one of two country code json keys.
      const countryCode = srcGeoip.country_code || srcGeoip.country_code2;
      if (!srcGeoip.region_name && !srcGeoip.timezone && !countryCode) return undefined;

      let srcGeoipValue = '';
      if (srcGeoip.timezone && !srcGeoip.region_name && !countryCode) {
        srcGeoipValue = srcGeoip.timezone;
      } else {
        if (countryCode && !srcGeoip.region_name) {
          srcGeoipValue = countryCode;
        } else if (srcGeoip.region_name && !countryCode) {
          srcGeoipValue = srcGeoip.region_name;
        } else {
          srcGeoipValue = `${srcGeoip.region_name}, ${countryCode}`;
        }
        if (srcGeoip.timezone) {
          srcGeoipValue += `; ${srcGeoip.timezone}`;
        }
      }
      return srcGeoipValue;
    },
  },
  [eventFields.geoipSourceContinentCode]: {
    path: 'src_geoip.continent_code',
  },
  [eventFields.geoipSourceCountryCode]: {
    // The api has the possiblity of returning one of two country code json keys.
    getDataValue: d => d.src_geoip && (d.src_geoip.country_code2 || d.src_geoip.country_code),
  },
  [eventFields.geoipSourceRegionCode]: {
    path: 'src_geoip.region_code',
  },
  [eventFields.geoipSourceRegionName]: {
    path: 'src_geoip.region_name',
  },
  [eventFields.geoipSourceTimezone]: {
    path: 'src_geoip.timezone',
  },
  [eventFields.idpInitiated]: {
    path: 'idp_initiated',
  },
  [eventFields.initiatedBy]: {
    getDataValue: (d) => {
      if (d.initiated_by) {
        return d.initiated_by.username || d.initiated_by.email;
      }
      return d.username;
    },
  },
  [eventFields.initiatedByEmail]: {
    path: 'initiated_by.email',
  },
  [eventFields.initiatedByType]: {
    path: 'initiated_by.type',
  },
  [eventFields.initiatedByUsername]: {
    path: 'initiated_by.username',
  },
  [eventFields.ldapObjectId]: {
    path: 'oid',
  },
  [eventFields.logMessage]: {
    path: 'message',
  },
  [eventFields.mdmDeviceId]: {
    path: 'mdm_device_id',
  },
  [eventFields.mfa]: {
    path: 'mfa',
  },
  [eventFields.mfaType]: {
    path: 'mfa_meta.type',
  },
  [eventFields.mfaState]: {
    path: 'nas_mfa_state',
  },
  [eventFields.numResults]: {
    path: 'number_of_results',
  },
  [eventFields.operationNumber]: {
    path: 'operation_number',
  },
  [eventFields.organization]: {
    path: 'organization',
  },
  [eventFields.outerEapType]: {
    path: 'outer.eap_type',
  },
  [eventFields.outerErrorMessage]: {
    path: 'outer.error_message',
  },
  [eventFields.outerUsername]: {
    path: 'outer.username',
  },
  [eventFields.processName]: {
    path: 'process_name',
  },
  [eventFields.provider]: {
    path: 'provider',
  },
  [eventFields.radiusCertificateAuthorityKeyId]: {
    path: 'auth_meta.authority_keyid',
  },
  [eventFields.radiusCertificateUserIdLocation]: {
    path: 'auth_meta.userid_location',
  },
  [eventFields.radiusCertificateIssuer]: {
    path: 'auth_meta.issuer',
  },
  [eventFields.radiusCertificateSerialNumber]: {
    path: 'auth_meta.cert_serial',
  },
  [eventFields.radiusPasswordEnabled]: {
    path: 'auth_meta.user_password_enabled',
  },
  [eventFields.radiusUserCertsEnabled]: {
    path: 'auth_meta.user_cert_enabled',
  },
  [eventFields.requestType]: {
    path: 'request_type',
  },
  [eventFields.resourceDisplayName]: {
    path: 'resource.display_name',
  },
  [eventFields.resourceDomain]: {
    path: 'resource.domain',
  },
  [eventFields.resourceEmail]: {
    path: 'resource.email',
  },
  [eventFields.resourceHostname]: {
    path: 'resource.hostname',
  },
  [eventFields.resourceId]: {
    path: 'resource.id',
  },
  [eventFields.resourceName]: {
    path: 'resource.name',
  },
  [eventFields.resourceRecipientEmail]: {
    path: 'resource.recipient_email',
  },
  [eventFields.resourceEmailType]: {
    path: 'resource.email_type',
  },
  [eventFields.resourceType]: {
    getDataValue: (d) => {
      if (d.event_type === EventTypes.integrationattributeExclude
        || d.event_type === EventTypes.integrationattributeInclude
        || !d.resource) {
        return undefined;
      }
      return d.resource.type;
    },
    path: 'resource.type',
  },
  [eventFields.resourceUsername]: {
    path: 'resource.username',
  },
  [eventFields.result]: {
    getDataValue: getResultFromRecord,
  },
  [eventFields.scope]: {
    path: 'scope',
  },
  [eventFields.searchFilter]: {
    path: 'filter',
  },
  [eventFields.service]: {
    path: 'service',
  },
  [eventFields.sourceIp]: {
    path: 'src_ip',
  },
  [eventFields.startTls]: {
    path: 'start_tls',
  },
  [eventFields.status]: {
    path: 'status',
  },
  [eventFields.success]: {
    path: 'success',
  },
  [eventFields.system]: {
    filterCriteria: value => ({
      or: [
        { 'system.displayName': value },
        {
          and: [
            { 'resource.displayName': value },
            { 'resource.type': resourceTypes.system },
          ],
        }, {
          and: [
            { 'association.connection.from.name': value },
            { 'association.connection.from.type': resourceValues.system },
          ],
        }, {
          and: [
            { 'association.connection.to.name': value },
            { 'association.connection.to.type': resourceValues.system },
          ],
        },
      ],
    }),
    paths: [
      { path: 'system.displayName' },
      {
        filter: { 'resource.type': resourceTypes.system },
        path: 'resource.displayName',
      },
      {
        filter: { 'association.connection.from.type': resourceValues.system },
        path: 'association.connection.from.name',
      }, {
        filter: { 'association.connection.to.type': resourceValues.system },
        path: 'association.connection.to.name',
      },
    ],
  },
  [eventFields.systemHostname]: {
    path: 'system.hostname',
  },
  [eventFields.systemOsFamily]: {
    path: 'useragent.os',
  },
  [eventFields.systemId]: {
    path: 'system.id',
  },
  [eventFields.systemTimestamp]: {
    path: 'system_timestamp',
  },
  [eventFields.timestamp]: {
    getDataValue: d => moment(d.timestamp).format(dateTimeSecondsFormat),
    path: 'timestamp',
  },
  [eventFields.tlsEstablished]: {
    path: 'tls_established',
  },
  [eventFields.user]: {
    filterCriteria: value => ({
      or: [
        { 'initiated_by.username': value },
        { 'resource.username': value },
        { username: value },
        {
          and: [
            { 'association.connection.from.name': value },
            { 'association.connection.from.type': resourceValues.user },
          ],
        }, {
          and: [
            { 'association.connection.to.name': value },
            { 'association.connection.to.type': resourceValues.user },
          ],
        },
      ],
    }),
    paths: [
      { path: 'initiated_by.username' },
      { path: 'resource.username' },
      { path: 'username' },
      {
        filter: { 'association.connection.from.type': resourceValues.user },
        path: 'association.connection.from.name',
      }, {
        filter: { 'association.connection.to.type': resourceValues.user },
        path: 'association.connection.to.name',
      },
    ],
  },
  [eventFields.userAgent]: {
    getDataValue: (d) => {
      const userAgent = d.user_agent;
      if (!userAgent) return undefined;

      let value = '';
      if (userAgent.os) {
        value = userAgent.os;
        if (userAgent.os_major) {
          value += ` ${userAgent.os_major}`;
          if (userAgent.os_minor) value += `.${userAgent.os_minor}`;
        }
      }
      if (userAgent.name) {
        if (value) value += ', ';
        value += userAgent.name;
        if (userAgent.major) {
          value += ` ${userAgent.major}`;
          if (userAgent.minor) {
            value += `.${userAgent.minor}`;
            if (userAgent.patch) value += `.${userAgent.patch}`;
          }
        }
      }
      return value;
    },
  },
  [eventFields.userIdType]: {
    path: 'auth_meta.userid_type',
  },
  [eventFields.username]: {
    path: 'username',
  },
  [eventFields.windowsElevated]: {
    path: 'windows_meta.elevated',
  },
  [eventFields.windowsLogonType]: {
    path: 'windows_meta.logon_type',
  },
};

function getUsername(record) {
  return metadata[eventFields.initiatedByUsername].getDataValue(record)
    || metadata[eventFields.initiatedByEmail].getDataValue(record);
}

/**
 * This object contains metadata for each event type.
 * It is used to map the icons and sentence summaries to the events.
 * @param {String} service - The event service used to map default service icons.
 * @param {Function} customIcons - Optional. If custom icons are designated,
 * it will override the default event service icons. Pass in record to keep record context.
 * @param {Number} customMiniCardLayout - Optional. Designate a custom layout for events
 * using the numerical reference to the layout in the link below:
 * @see https://www.figma.com/file/x4FiaK2nR9lUEM1JjDTDWm/%F0%9F%91%912021-Directory-Insights-Master?node-id=3086%3A118473
 * @param {Function} summary - A function that returns the custom summary string for events.
 * Pass in record to keep record context.
 */
export const eventTypeMetadata = {
  [EventTypes.activeDirectoryCreate]: {
    service: eventServices.directory,
    summary: record => (record.initiated_by?.email ? `An active directory instance was created by user, ${record.initiated_by.email}.` : 'An active directory instance was created.'),
  },
  [EventTypes.activeDirectoryDelete]: {
    service: eventServices.directory,
    summary: record => (record.initiated_by?.email ? `An active directory instance was deleted by user, ${record.initiated_by.email}.` : 'An active directory instance was deleted.'),
  },
  [EventTypes.adminCreate]: {
    service: eventServices.directory,
    summary: record => {
      const adminEmail = metadata[eventFields.resourceEmail].getDataValue(record);
      return adminEmail ? `The admin, ${adminEmail}, was created.` : 'The admin was created.';
    },
  },
  [EventTypes.adminDelete]: {
    service: eventServices.directory,
    summary: record => {
      const adminEmail = metadata[eventFields.resourceEmail].getDataValue(record);
      return adminEmail ? `The admin, ${adminEmail}, was deleted.` : 'The admin was deleted.';
    },
  },
  [EventTypes.adminLoginAttempt]: {
    customIcons: record => ({
      arrow: arrowIcon,
      center: String(record.success) === 'true' ? loginSuccessIcon : loginFailIcon,
      left: userIcon,
      right: portalIcon,
    }),
    service: eventServices.directory,
    summary: record => {
      const adminName = metadata[eventFields.initiatedBy].getDataValue(record);
      return `${adminName ? `The admin, ${adminName},` : 'An admin'} ${record.success ? 'was logged' : 'failed to log'} in to Admin Portal`;
    },
  },
  [EventTypes.adminOldApiKeyAttempt]: {
    service: eventServices.directory,
    summary: () => 'API usage attempt failed with an old API key.',
  },
  [EventTypes.adminPasswordChange]: {
    service: eventServices.directory,
    summary: record => {
      const initiatedByEmail = metadata[eventFields.initiatedByEmail].getDataValue(record);
      const resourceEmail = metadata[eventFields.resourceEmail].getDataValue(record);
      // use the initiatedBy email value if it is found, otherwise grab the resource email
      // both should be the same since this is an admin changing their own password, but
      // initiatedBy may not have been included in the event
      const adminEmail = initiatedByEmail || resourceEmail;

      // this allows us to treat a missing record.success as a successful password change
      // since only a failed attempt includes the success field (set to false)
      return `The admin, ${adminEmail}, ${String(record.success) === 'false' ? 'failed to change' : 'changed'} their password.`;
    },
  },
  [EventTypes.adminPasswordResetRequest]: {
    service: eventServices.directory,
    summary: record => (record.resource?.email ? `Admin password reset was requested by user, ${record.resource.email}.` : 'Admin password reset was requested.'),
  },
  [EventTypes.adminUpdate]: {
    service: eventServices.directory,
    summary: record => {
      const adminEmail = metadata[eventFields.resourceEmail].getDataValue(record);
      return adminEmail ? `The admin, ${adminEmail}, was updated.` : 'The admin was updated.';
    },
  },
  [EventTypes.applicationCreate]: {
    customMiniCardLayout: 1,
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.displayName && !record.initiated_by?.email) return 'The application was created.';
      if (!record.resource?.displayName) return `The application was created by user ${record.initiated_by.email}.`;
      if (!record.initiated_by?.email) return `The application, ${record.resource.displayName}, was created.`;
      return `The application, ${record.resource.displayName}, was created by user ${record.initiated_by.email}.`;
    },
  },
  [EventTypes.applicationDelete]: {
    customMiniCardLayout: 1,
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.displayName && !record.initiated_by?.email) return 'The application was deleted.';
      if (!record.resource?.displayName) return `The application was deleted by user ${record.initiated_by.email}.`;
      if (!record.initiated_by?.email) return `The application, ${record.resource.displayName}, was deleted.`;
      return `The application, ${record.resource.displayName}, was deleted by user ${record.initiated_by.email}.`;
    },
  },
  [EventTypes.applicationUpdate]: {
    customMiniCardLayout: 1,
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.displayName && !record.initiated_by?.email) return 'The application was updated.';
      if (!record.resource?.displayName) return `The application was updated by user ${record.initiated_by.email}.`;
      if (!record.initiated_by?.email) return `The application, ${record.resource.displayName}, was updated.`;
      return `The application, ${record.resource.displayName}, was updated by user ${record.initiated_by.email}.`;
    },
  },
  [EventTypes.associationChange]: {
    service: eventServices.directory,
    summary: record => {
      const fromType = get(record, 'association.connection.from.type');
      const toType = get(record, 'association.connection.to.type');

      const parentType = resourceToTextMap[fromType.toUpperCase()];
      const childType = resourceToTextMap[toType.toUpperCase()];
      const parentTypeGeneric = resourceToGenericTextMap[fromType];
      const childTypeGeneric = resourceToGenericTextMap[toType];
      const parentName = get(record, 'association.connection.from.name');
      const childName = get(record, 'association.connection.to.name');

      let action = '';

      const op = get(record, 'association.op', '');

      if (op === 'remove') {
        action = 'removed from';
      } else if (op === 'add') {
        action = 'added to';
      } else if (op === 'update') {
        action = 'updated on';
      }

      if (!childType || !parentType || !action) return '';

      const child = childName || childTypeGeneric;
      const parent = parentName ? `the ${parentType}, ${parentName}` : parentTypeGeneric.toLowerCase();

      return `${child} was ${action} ${parent}`;
    },
  },
  [EventTypes.authnPolicyCreate]: {
    customMiniCardLayout: 7,
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.name && !record.initiated_by?.email) return 'The authpolicy was created.';
      if (!record.resource?.name) return `The authpolicy was created by user ${record.initiated_by.email}.`;
      if (!record.initiated_by?.email) return `The authpolicy, ${record.resource.name}, was created.`;
      return `The authpolicy, ${record.resource.name}, was created by user ${record.initiated_by.email}.`;
    },
  },
  [EventTypes.authnPolicyDelete]: {
    customMiniCardLayout: 7,
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.name && !record.initiated_by?.email) return 'The authpolicy was deleted.';
      if (!record.resource?.name) return `The authpolicy was deleted by user ${record.initiated_by.email}.`;
      if (!record.initiated_by?.email) return `The authpolicy, ${record.resource.name}, was deleted.`;
      return `The authpolicy, ${record.resource.name}, was deleted by user ${record.initiated_by.email}.`;
    },
  },
  [EventTypes.authnPolicyUpdate]: {
    customMiniCardLayout: 7,
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.name && !record.initiated_by?.email) return 'The authpolicy was updated.';
      if (!record.resource?.name) return `The authpolicy was updated by user ${record.initiated_by.email}.`;
      if (!record.initiated_by?.email) return `The authpolicy, ${record.resource.name}, was updated.`;
      return `The authpolicy, ${record.resource.name}, was updated by user ${record.initiated_by.email}.`;
    },
  },
  [EventTypes.commandCreate]: {
    customMiniCardLayout: 1,
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.name && !record.initiated_by?.email) return 'The command was created.';
      if (!record.resource?.name) return `The command was created by user ${record.initiated_by.email}.`;
      if (!record.initiated_by?.email) return `The command, ${record.resource.name}, was created.`;
      return `The command, ${record.resource.name}, was created by user ${record.initiated_by.email}.`;
    },
  },
  [EventTypes.commandDelete]: {
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.name && !record.initiated_by?.email) return 'The command was deleted.';
      if (!record.resource?.name) return `The command was deleted by user ${record.initiated_by.email}.`;
      if (!record.initiated_by?.email) return `The command, ${record.resource.name}, was deleted.`;
      return `The command, ${record.resource.name}, was deleted by user ${record.initiated_by.email}.`;
    },
  },
  [EventTypes.commandResultDelete]: {
    service: eventServices.directory,
    summary: record => (record.resource?.name ? `The command result, ${record.resource.name}, was deleted.` : 'The command result was deleted.'),
  },
  [EventTypes.commandRun]: {
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.name && !record.initiated_by?.email) return 'The command was run.';
      if (!record.resource?.name) return `The command was run by user ${record.initiated_by.email}.`;
      if (!record.initiated_by?.email) return `The command, ${record.resource.name}, was run.`;
      return `The command, ${record.resource.name}, was run by user ${record.initiated_by.email}.`;
    },
  },
  [EventTypes.commandUpdate]: {
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.name && !record.initiated_by?.email) return 'The command was updated.';
      if (!record.resource?.name) return `The command was updated by user ${record.initiated_by.email}.`;
      if (!record.initiated_by?.email) return `The command, ${record.resource.name}, was updated.`;
      return `The command, ${record.resource.name}, was updated by user ${record.initiated_by.email}.`;
    },
  },
  [EventTypes.fileCreate]: {
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.name && !record.initiated_by?.email) return 'The file was created.';
      if (!record.resource?.name) return `The file was created by user ${record.initiated_by.email}.`;
      if (!record.initiated_by?.email) return `The file, ${record.resource.name}, was created.`;
      return `The file, ${record.resource.name}, was created by user ${record.initiated_by.email}.`;
    },
  },
  [EventTypes.fileDelete]: {
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.name && !record.initiated_by?.email) return 'The file was deleted.';
      if (!record.resource?.name) return `The file was deleted by user ${record.initiated_by.email}.`;
      if (!record.initiated_by?.email) return `The file, ${record.resource.name}, was deleted.`;
      return `The file, ${record.resource.name}, was deleted by user ${record.initiated_by.email}.`;
    },
  },
  [EventTypes.fileUpdate]: {
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.name && !record.initiated_by?.email) return 'The file was updated.';
      if (!record.resource?.name) return `The file was updated by user ${record.initiated_by.email}.`;
      if (!record.initiated_by?.email) return `The file, ${record.resource.name}, was updated.`;
      return `The file, ${record.resource.name}, was updated by user ${record.initiated_by.email}.`;
    },
  },
  [EventTypes.groupCreate]: {
    customMiniCardLayout: 1,
    service: eventServices.directory,
    summary: record => (record.resource?.name ? `The user group, ${record.resource.name}, was created.` : 'A user group was created'),
  },
  [EventTypes.groupDelete]: {
    customMiniCardLayout: 1,
    service: eventServices.directory,
    summary: record => (record.resource?.name ? `The user group, ${record.resource.name}, was deleted.` : 'A user group was deleted'),
  },
  [EventTypes.groupUpdate]: {
    customMiniCardLayout: 1,
    service: eventServices.directory,
    summary: record => (record.resource?.name ? `The user group, ${record.resource.name}, was updated.` : 'A user group was updated'),
  },
  [EventTypes.gsuiteDirectoryCreate]: {
    customMiniCardLayout: 9,
    service: eventServices.directory,
    summary: record => (record.initiated_by?.email ? `A gsuite directory was created by user, ${record.initiated_by.email}.` : 'A gsuite directory was created.'),
  },
  [EventTypes.gsuiteDirectoryDelete]: {
    customMiniCardLayout: 9,
    service: eventServices.directory,
    summary: record => (record.initiated_by?.email ? `A gsuite directory was deleted by user, ${record.initiated_by.email}.` : 'A gsuite directory was deleted.'),
  },
  [EventTypes.gsuiteDirectoryUpdate]: {
    customMiniCardLayout: 9,
    service: eventServices.directory,
    summary: record => (record.initiated_by?.email ? `A gsuite directory was updated by user, ${record.initiated_by.email}.` : 'A gsuite directory was updated.'),
  },
  [EventTypes.idpCreate]: {
    service: eventServices.directory,
    summary: record => (record.resource?.name ? `The identity provider, ${record.resource.name}, was created.` : 'The identity provider was created.'),
  },
  [EventTypes.idpDelete]: {
    service: eventServices.directory,
    summary: record => (record.resource?.name ? `The identity provider, ${record.resource.name}, was deleted.` : 'The identity provider was deleted.'),
  },
  [EventTypes.idpUpdate]: {
    service: eventServices.directory,
    summary: record => (record.resource?.name ? `The identity provider, ${record.resource.name}, was updated.` : 'The identity provider was updated.'),
  },
  [EventTypes.idSourceCreate]: {
    service: eventServices.directory,
    summary: () => 'An ID Source was created.',
  },
  [EventTypes.idSourceDelete]: {
    service: eventServices.directory,
    summary: () => 'An ID Source was deleted.',
  },
  [EventTypes.idSourceUpdate]: {
    service: eventServices.directory,
    summary: () => 'An ID Source was updated.',
  },
  [EventTypes.integrationActivate]: {
    customMiniCardLayout: 8,
    service: eventServices.directory,
    summary: record => (record.initiated_by?.email ? `An integration was activated by user ${record.initiated_by.email}.` : 'An integration was activated.'),
  },
  [EventTypes.integrationattributeExclude]: {
    service: eventServices.directory,
    summary: record => (record.resource?.name ? `The integration attribute, ${record.resource.name}, was excluded.` : 'An integration attribute was excluded.'),
  },
  [EventTypes.integrationattributeInclude]: {
    service: eventServices.directory,
    summary: record => (record.resource?.name ? `The integration attribute, ${record.resource.name}, was included.` : 'An integration attribute was included.'),
  },
  [EventTypes.integrationDeactivate]: {
    customMiniCardLayout: 8,
    service: eventServices.directory,
    summary: record => (record.initiated_by?.email ? `An integration was deactivated by user ${record.initiated_by.email}.` : 'An integration was deactivated.'),
  },
  [EventTypes.invoiceDownload]: {
    service: eventServices.directory,
    summary: record => {
      const id = metadata[eventFields.resourceId].getDataValue(record);
      return id ? `Invoice ${id} was downloaded from the Admin Portal.` : 'An invoice was downloaded from the Admin Portal.';
    },
  },
  [EventTypes.integrationDefaultDomainAdd]: {
    customMiniCardLayout: 9,
    service: eventServices.directory,
  },
  [EventTypes.integrationDefaultDomainDelete]: {
    customMiniCardLayout: 9,
    service: eventServices.directory,
  },
  [EventTypes.integtrationDefaultDomainUpdate]: {
    customMiniCardLayout: 9,
    service: eventServices.directory,
  },
  [EventTypes.integrationDomainAdd]: {
    customMiniCardLayout: 9,
    service: eventServices.directory,
  },
  [EventTypes.integrationDomainDelete]: {
    customMiniCardLayout: 9,
    service: eventServices.directory,
  },
  [EventTypes.iplistCreate]: {
    service: eventServices.directory,
    summary: record => (record.initiated_by?.email ? `The IP address list was created by user, ${record.initiated_by.email}.` : 'The IP address list was created.'),
  },
  [EventTypes.iplistDelete]: {
    service: eventServices.directory,
    summary: record => (record.initiated_by?.email ? `The IP address list was deleted by user, ${record.initiated_by.email}.` : 'The IP address list was deleted.'),
  },
  [EventTypes.iplistUpdate]: {
    service: eventServices.directory,
    summary: record => (record.initiated_by?.email ? `The IP address list was updated by user, ${record.initiated_by.email}.` : 'The IP address list was updated.'),
  },
  [EventTypes.ldapBind]: {
    service: eventServices.ldap,
    summary: record => (record.success ? 'An ldap bind occurred.' : 'An ldap bind failed.'),
  },
  [EventTypes.ldapSearch]: {
    service: eventServices.ldap,
    summary: () => 'An ldap search occurred.',
  },
  [EventTypes.loginAttempt]: {
    customIcons: record => ({
      arrow: arrowIcon,
      center: String(record.success) === 'true' ? loginSuccessIcon : loginFailIcon,
      left: userIcon,
      right: devicesIcon,
    }),
    service: eventServices.systems,
    summary: record => {
      const username = metadata[eventFields.username].getDataValue(record);
      const systemName = metadata[eventFields.systemHostname].getDataValue(record);

      return `${username ? `The user, ${username},` : 'A user'} ${String(record.success) === 'true' ? 'was logged' : 'failed to log'} in to the Device${`, ${systemName}` || ''}`;
    },
  },
  [EventTypes.mdmCommandResult]: {
    service: eventServices.mdm,
    summary: record => {
      // convert request type from Pascal case to add spaces before capital letters
      const requestType = startCase(record.request_type);
      const status = record.status.toLowerCase();

      return requestType && status ? `The MDM command ${requestType} ${status === 'acknowledged' ? 'was acknowledged' : 'errored'}.` : '';
    },
  },
  [EventTypes.notificationDelete]: {
    service: eventServices.directory,
    summary: () => 'A notification was deleted.',
  },
  [EventTypes.o365DirectoryCreate]: {
    customMiniCardLayout: 9,
    service: eventServices.directory,
    summary: record => (record.initiated_by?.email ? `An o365 directory was created by user, ${record.initiated_by.email}.` : 'An o365 directory was created.'),
  },
  [EventTypes.o365DirectoryDelete]: {
    customMiniCardLayout: 9,
    service: eventServices.directory,
    summary: record => (record.initiated_by?.email ? `An o365 directory was deleted by user, ${record.initiated_by.email}.` : 'An o365 directory was deleted.'),
  },
  [EventTypes.o365DirectoryUpdate]: {
    customMiniCardLayout: 9,
    service: eventServices.directory,
    summary: record => (record.initiated_by?.email ? `An o365 directory was updated by user, ${record.initiated_by.email}.` : 'An o365 directory was updated.'),
  },
  [EventTypes.organizationCreate]: {
    service: eventServices.directory,
    summary: record => {
      const orgName = get(record, 'resource.displayName');
      const directObject = orgName ? `The organization, ${orgName},` : 'An organization';
      return `${directObject} was created.`;
    },
  },
  [EventTypes.organizationUpdate]: {
    service: eventServices.directory,
    summary: record => {
      const orgName = get(record, 'resource.displayName');
      const directObject = orgName ? `the organization, ${orgName}` : 'an organization';
      const numFieldChanges = record.changes.length;

      if (numFieldChanges === 1) {
        const fieldChanged = get(record, 'changes.0.field');

        if (fieldChanged) {
          return `The ${fieldChanged} field was updated on ${directObject}`;
        }
      }

      return `${numFieldChanges} ${numFieldChanges === 1 ? 'field was' : 'fields were'} updated on ${directObject}`;
    },
  },
  [EventTypes.osMajorUpgrade]: {
    customIcons: () => ({
      arrow: arrowIcon,
      center: upgraded,
      left: singleDevice,
      right: deviceUpgraded,
    }),
    service: eventServices.systems,
    summary: record => {
      if (!record.system?.displayName && !record.system?.hostname && !record.changes[0]?.from && !record.changes[0].to) return 'The system was upgraded.';
      if (!record.system?.displayName && !record.system?.hostname) return `The system was upgraded from ${record.changes[0].from || ''} to ${record.changes[0].to || ''}.`;
      if (!record.changes[0]?.from && !record.changes[0]?.to) return `The system, ${record.system.displayName || record.system.hostname}, was upgraded.`;
      if (!record.changes[0]?.from) return `The system, ${record.system.displayName || record.system.hostname}, was upgraded to ${record.changes[0].to || ''}.`;
      if (!record.changes[0]?.to) return `The system, ${record.system.displayName || record.system.hostname}, was upgraded from ${record.changes[0].from || ''}.`;
      return `The system, ${record.system.displayName || record.system.hostname}, was upgraded from ${record.changes[0].from || ''} to ${record.changes[0].to || ''}.`;
    },
  },
  [EventTypes.osMinorUpgrade]: {
    customIcons: () => ({
      arrow: arrowIcon,
      center: upgraded,
      left: singleDevice,
      right: deviceUpgraded,
    }),
    service: eventServices.systems,
    summary: record => {
      if (!record.system?.displayName && !record.system?.hostname && !record.changes[0]?.from && !record.changes[0].to) return 'The system was upgraded.';
      if (!record.system?.displayName && !record.system?.hostname) return `The system was upgraded from ${record.changes[0].from || ''} to ${record.changes[0].to || ''}.`;
      if (!record.changes[0]?.from && !record.changes[0]?.to) return `The system, ${record.system.displayName || record.system.hostname}, was upgraded.`;
      if (!record.changes[0]?.from) return `The system, ${record.system.displayName || record.system.hostname}, was upgraded to ${record.changes[0].to || ''}.`;
      if (!record.changes[0]?.to) return `The system, ${record.system.displayName || record.system.hostname}, was upgraded from ${record.changes[0].from || ''}.`;
      return `The system, ${record.system.displayName || record.system.hostname}, was upgraded from ${record.changes[0].from || ''} to ${record.changes[0].to || ''}.`;
    },
  },
  [EventTypes.osMajorRollback]: {
    customIcons: () => ({
      arrow: arrowIcon,
      center: recordUpdate,
      left: userIcon,
      right: portalIcon,
    }),
    service: eventServices.systems,
    summary: record => {
      if (!record.system?.displayName && !record.system?.hostname && !record.changes[0]?.from && !record.changes[0].to) return 'The system was rolled back.';
      if (!record.system?.displayName && !record.system?.hostname) return `The system was rolled back from ${record.changes[0].from || ''} to ${record.changes[0].to || ''}.`;
      if (!record.changes[0]?.from && !record.changes[0]?.to) return `The system, ${record.system.displayName || record.system.hostname}, was rolled back.`;
      if (!record.changes[0]?.from) return `The system, ${record.system.displayName || record.system.hostname}, was rolled back to ${record.changes[0].to || ''}.`;
      if (!record.changes[0]?.to) return `The system, ${record.system.displayName || record.system.hostname}, was rolled back from ${record.changes[0].from || ''}.`;
      return `The system, ${record.system.displayName || record.system.hostname}, was rolled back from ${record.changes[0].from || ''} to ${record.changes[0].to || ''}.`;
    },
  },
  [EventTypes.osMinorRollback]: {
    customIcons: () => ({
      arrow: arrowIcon,
      center: recordUpdate,
      left: userIcon,
      right: portalIcon,
    }),
    service: eventServices.systems,
    summary: record => {
      if (!record.system?.displayName && !record.system?.hostname && !record.changes[0]?.from && !record.changes[0].to) return 'The system was rolled back.';
      if (!record.system?.displayName && !record.system?.hostname) return `The system was rolled back from ${record.changes[0].from || ''} to ${record.changes[0].to || ''}.`;
      if (!record.changes[0]?.from && !record.changes[0]?.to) return `The system, ${record.system.displayName || record.system.hostname}, was rolled back.`;
      if (!record.changes[0]?.from) return `The system, ${record.system.displayName || record.system.hostname}, was rolled back to ${record.changes[0].to || ''}.`;
      if (!record.changes[0]?.to) return `The system, ${record.system.displayName || record.system.hostname}, was rolled back from ${record.changes[0].from || ''}.`;
      return `The system, ${record.system.displayName || record.system.hostname}, was rolled back from ${record.changes[0].from || ''} to ${record.changes[0].to || ''}.`;
    },
  },
  [EventTypes.passwordManagerDisable]: {
    service: eventServices.password_manager,
    customMiniCardLayout: 7,
    summary: () => 'Password Manager is disabled.',
  },
  [EventTypes.passwordManagerEnable]: {
    service: eventServices.password_manager,
    customMiniCardLayout: 7,
    summary: () => 'Password Manager is enabled.',
  },
  [EventTypes.passwordManagerFolderCreate]: {
    service: eventServices.password_manager,
    customMiniCardLayout: 7,
    summary: () => 'Password Manager folder created.',
  },
  [EventTypes.passwordManagerFolderDelete]: {
    service: eventServices.password_manager,
    customMiniCardLayout: 7,
    summary: () => 'Password Manager folder deleted.',
  },
  [EventTypes.passwordManagerFolderMemberAdd]: {
    service: eventServices.password_manager,
    customMiniCardLayout: 7,
    summary: () => 'Member added to Password Manager folder.',
  },
  [EventTypes.passwordManagerFolderMemberRemove]: {
    service: eventServices.password_manager,
    customMiniCardLayout: 7,
    summary: () => 'Member removed from Password Manager folder.',
  },
  [EventTypes.passwordManagerFolderMemberUpdate]: {
    service: eventServices.password_manager,
    customMiniCardLayout: 7,
    summary: () => 'Member updated in Password Manager folder.',
  },
  [EventTypes.passwordManagerUserActivate]: {
    service: eventServices.password_manager,
    customMiniCardLayout: 7,
    summary: () => 'Password Manager user activated.',
  },
  [EventTypes.passwordManagerUserDeactivate]: {
    service: eventServices.password_manager,
    customMiniCardLayout: 7,
    summary: () => 'Password Manager user deactivated.',
  },
  [EventTypes.passwordManagerUserDisable]: {
    service: eventServices.password_manager,
    customMiniCardLayout: 7,
    summary: () => 'Password Manager user disabled.',
  },
  [EventTypes.passwordManagerUserEnable]: {
    service: eventServices.password_manager,
    customMiniCardLayout: 7,
    summary: () => 'Password Manager user enabled.',
  },
  [EventTypes.passwordManagerUserReactivate]: {
    service: eventServices.password_manager,
    customMiniCardLayout: 7,
    summary: () => 'Password Manager user reactivated.',
  },
  [EventTypes.policyCreate]: {
    customMiniCardLayout: 1,
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.name && !record.initiated_by?.email) return 'A policy was created.';
      if (!record.resource?.name) return `A policy was created by user ${record.initiated_by.email}.`;
      if (!record.initiated_by?.email) return `The policy, ${record.resource.name}, was created.`;
      return `The policy, ${record.resource.name}, was created by user ${record.initiated_by.email}.`;
    },
  },
  [EventTypes.policyDelete]: {
    customMiniCardLayout: 1,
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.name && !record.initiated_by?.email) return 'A policy was deleted.';
      if (!record.resource?.name) return `A policy was deleted by user ${record.initiated_by.email}.`;
      if (!record.initiated_by?.email) return `The policy, ${record.resource.name}, was deleted.`;
      return `The policy, ${record.resource.name}, was deleted by user ${record.initiated_by.email}.`;
    },
  },
  [EventTypes.policyUpdate]: {
    customMiniCardLayout: 1,
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.name && !record.initiated_by?.email) return 'A policy was updated.';
      if (!record.resource?.name) return `A policy was updated by user ${record.initiated_by.email}.`;
      if (!record.initiated_by?.email) return `The policy, ${record.resource.name}, was updated.`;
      return `The policy, ${record.resource.name}, was updated by user ${record.initiated_by.email}.`;
    },
  },
  [EventTypes.radiusAuth]: {
    service: eventServices.radius,
    summary: record => (record.initiated_by.username ? `RADIUS login ${record.success ? 'successfully made' : 'failed'} by user, ${record.initiated_by.username}.` : `RADIUS login ${record.success ? 'successfully made' : 'failed'}`),
  },
  [EventTypes.radiusAuthAttempt]: {
    service: eventServices.radius,
    summary: record => (record.initiated_by.username ? `RADIUS login ${record.success ? 'successfully made' : 'failed'} by user, ${record.initiated_by.username}.` : `RADIUS login ${record.success ? 'successfully made' : 'failed'}`),
  },
  [EventTypes.radiusAuthAttempt]: {
    service: eventServices.radius,
    summary: record => (record.initiated_by.username ? `RADIUS login ${record.success ? 'successfully made' : 'failed'} by user, ${record.initiated_by.username}.` : `RADIUS login ${record.success ? 'successfully made' : 'failed'}`),
  },
  [EventTypes.radiusServerCreate]: {
    customMiniCardLayout: 1,
    service: eventServices.directory,
    summary: record => (record.resource?.name ? `The radiusserver, ${record.resource.name}, was created.` : 'A radiusserver was created'),
  },
  [EventTypes.radiusServerDelete]: {
    customMiniCardLayout: 1,
    service: eventServices.directory,
    summary: record => (record.resource?.name ? `The radiusserver, ${record.resource.name}, was deleted.` : 'A radiusserver was deleted'),
  },
  [EventTypes.radiusServerUpdate]: {
    customMiniCardLayout: 1,
    service: eventServices.directory,
    summary: record => (record.resource?.name ? `The radiusserver, ${record.resource.name}, was updated.` : 'A radiusserver was updated'),
  },
  [EventTypes.remoteSessionEnd]: {
    service: eventServices.systems,
    summary: () => 'Remote session ended.',
  },
  [EventTypes.remoteSessionFeedback]: {
    service: eventServices.systems,
    summary: () => 'Remote session feedback submitted.',
  },
  [EventTypes.remoteSessionJoin]: {
    service: eventServices.systems,
    summary: () => 'Remote session joined.',
  },
  [EventTypes.remoteSessionStart]: {
    service: eventServices.systems,
    summary: () => 'Remote session started.',
  },
  [EventTypes.sambaDomainCreate]: {
    service: eventServices.directory,
    summary: record => (record.resource?.name ? `Samba was enabled for ${record.resource.name} directory.` : 'Samba was enabled.'),
  },
  [EventTypes.sambaDomainDelete]: {
    service: eventServices.directory,
    summary: record => (record.resource?.name ? `Samba was disabled for/removed from ${record.resource.name} directory.` : 'Samba was disabled/removed.'),
  },
  [EventTypes.sambaDomainUpdate]: {
    service: eventServices.directory,
    summary: () => 'Samba configurations were updated.',
  },
  [EventTypes.softwareAdd]: {
    service: eventServices.software,
    summary: record => (record.application.name ? `The software ${record.application.name} was added.` : 'Software was added.'),
  },
  [EventTypes.softwareChange]: {
    service: eventServices.software,
    summary: record => (record.application.name ? `The software ${record.application.name} was changed.` : 'Software was changed.'),
  },
  [EventTypes.softwareRemove]: {
    service: eventServices.software,
    summary: record => (record.application.name ? `The software ${record.application.name} was removed.` : 'Software was removed.'),
  },
  [EventTypes.ssoAuth]: {
    service: eventServices.sso,
    summary: record => {
      if (!record.application?.display_label && !record.initiated_by?.username) return 'A user was authenticated.';
      if (!record.application?.display_label) return `The user, ${record.initiated_by.username}, authenticated.`;
      if (!record.initiated_by?.username) return `A user authenticated to application ${record.application.display_label}.`;
      return `The user, ${record.initiated_by.username}, authenticated to application, ${record.application.display_label}.`;
    },
  },
  [EventTypes.systemCreate]: {
    service: eventServices.directory,
    summary: record => {
      const systemName = metadata[eventFields.resourceDisplayName].getDataValue(record)
        || metadata[eventFields.resourceHostname].getDataValue(record);

      return systemName ? `The system, ${systemName}, was created.` : 'A system was created.';
    },
  },
  [EventTypes.systemDelete]: {
    service: eventServices.directory,
    summary: record => {
      const systemName = metadata[eventFields.resourceDisplayName].getDataValue(record)
      || metadata[eventFields.resourceHostname].getDataValue(record);

      return systemName ? `The system, ${systemName}, was deleted.` : 'A system was deleted.';
    },
  },
  [EventTypes.systemUpdate]: {
    customMiniCardLayout: 2,
    service: eventServices.directory,
    summary: record => {
      const systemName = metadata[eventFields.resourceDisplayName].getDataValue(record)
        || metadata[eventFields.resourceHostname].getDataValue(record);

      return systemName ? `The system, ${systemName}, was updated.` : 'A system was updated.';
    },
  },
  [EventTypes.systemFdeKeyDecrypt]: {
    service: eventServices.systems,
    summary: record => (record.system?.displayName ? `The system, ${record.system.displayName}, file disk encryption key decrypted.` : 'A system file disk encryption key decrypted.'),
  },
  [EventTypes.systemFdeKeyUpdate]: {
    service: eventServices.systems,
    summary: record => (record.system?.displayName ? `The system, ${record.system.displayName}, file disk encryption key updated.` : 'A system file disk encryption key updated.'),
  },
  [EventTypes.trialResume]: {
    service: eventServices.directory,
    summary: record => {
      const username = getUsername(record);
      const trialName = record.resource.name;
      const initiatorIsAdmin = record.initiated_by.type === 'admin';

      return initiatorIsAdmin
        ? `The admin, ${username}, resumed a trial for ${trialName}`
        : `Resumed a trial for ${trialName}`;
    },
  },
  [EventTypes.trialStart]: {
    service: eventServices.directory,
    summary: record => {
      const username = getUsername(record);
      const trialName = record.resource.name;
      const initiatorIsAdmin = record.initiated_by.type === 'admin';

      return initiatorIsAdmin
        ? `The admin, ${username}, started a trial for ${trialName}`
        : `Started a trial for ${trialName}`;
    },
  },
  [EventTypes.trialStop]: {
    service: eventServices.directory,
    summary: record => {
      const username = getUsername(record);
      const trialName = record.resource.name;
      const initiatorIsAdmin = record.initiated_by.type === 'admin';

      return initiatorIsAdmin
        ? `The admin, ${username}, stopped a trial for ${trialName}`
        : `Stopped a trial for ${trialName}`;
    },
  },
  [EventTypes.userActivated]: {
    service: eventServices.directory,
    summary: record => {
      const username = metadata[eventFields.resourceUsername].getDataValue(record);

      return `${username ? `The user, ${username},` : 'A user'} was activated.`;
    },
  },
  [EventTypes.userActivationEmail]: {
    customMiniCardLayout: 4,
    service: eventServices.directory,
    summary: () => 'User activation email was sent for user',
  },
  [EventTypes.userActivationScheduleCreate]: {
    service: eventServices.directory,
    summary: () => 'Activation scheduled for user',
  },
  [EventTypes.userActivationScheduleDelete]: {
    service: eventServices.directory,
    summary: () => 'Scheduled activation deleted for user',
  },
  [EventTypes.userAdminGranted]: {
    customMiniCardLayout: 6,
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.username && !record.system?.hostname) return 'A user was granted sudo access.';
      if (!record.resource?.username) return `A user was granted sudo access for system, ${record.system.hostname}.`;
      if (!record.system?.hostname) return `The user, ${record.resource.username}, was granted sudo access.`;
      return `The user, ${record.resource.username}, was granted sudo access for system, ${record.system.hostname}.`;
    },
  },
  [EventTypes.userAdminRevoked]: {
    customMiniCardLayout: 6,
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.username && !record.system?.hostname) return 'Sudo action was revoked from a user.';
      if (!record.resource?.username) return `Sudo action was revoked from a user for system, ${record.system.hostname}.`;
      if (!record.system?.hostname) return `Sudo access was revoked from the user, ${record.resource.username}.`;
      return `Sudo access was revoked from the user, ${record.resource.username}, for system, ${record.system.hostname}.`;
    },
  },
  [EventTypes.userCreate]: {
    service: eventServices.directory,
    summary: record => {
      const username = metadata[eventFields.resourceUsername].getDataValue(record)
      || find(record.changes, { field: 'email' }).to;

      return username ? `The user, ${username}, was created.` : 'A user was created.';
    },
  },
  [EventTypes.userCreateProvision]: {
    customMiniCardLayout: 5,
    service: eventServices.directory,
    summary: record => {
      if (!record.application?.name && !record.resource?.username) return 'A user created provisions.';
      if (!record.application?.name) return `The user, ${record.resource.username}, created provisions.`;

      // Capitalize application name
      const appName = startCase(record.application.name);

      if (!record.resource?.username) return `A user created provisions for ${appName}.`;
      return `The user, ${record.resource.username} created provisions for ${appName}.`;
    },
  },
  [EventTypes.userDelete]: {
    service: eventServices.directory,
    summary: record => {
      const username = metadata[eventFields.resourceUsername].getDataValue(record)
      || find(record.changes, { field: 'email' }).from;

      return username ? `The user, ${username}, was deleted.` : 'A user was deleted.';
    },
  },
  [EventTypes.userDeleteProvision]: {
    service: eventServices.directory,
    summary: record => {
      if (!record.application?.name && !record.resource?.username) return 'A user deleted provisions.';
      if (!record.application?.name) return `The user, ${record.resource.username}, deleted provisions.`;

      // Capitalize application name
      const appName = startCase(record.application.name);

      if (!record.resource?.username) return `A user deleted provisions for ${appName}.`;
      return `The user, ${record.resource.username} deleted provisions for ${appName}.`;
    },
  },
  [EventTypes.userGroupAdminGrant]: {
    customMiniCardLayout: 1,
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.name && !record.initiated_by?.email) return 'Administrative privileges were granted.';
      if (!record.resource?.name) return `Administrative privileges were granted by user ${record.initiated_by.email}.`;
      if (!record.initiated_by?.email) return `Administrative privileges were granted to, ${record.resource.name}.`;
      return `Administrative privileges were granted to, ${record.resource.name}, by user, ${record.initiated_by.email}.`;
    },
  },
  [EventTypes.userGroupAdminRevoke]: {
    customMiniCardLayout: 1,
    service: eventServices.directory,
    summary: record => {
      if (!record.resource?.name && !record.initiated_by?.email) return 'Administrative privileges were revoked.';
      if (!record.resource?.name) return `Administrative privileges were revoked by user ${record.initiated_by.email}.`;
      if (!record.initiated_by?.email) return `Administrative privileges were revoked from, ${record.resource.name}.`;
      return `Administrative privileges were revoked from, ${record.resource.name}, by user, ${record.initiated_by.email}.`;
    },
  },
  [EventTypes.userLockout]: {
    service: eventServices.directory,
    summary: record => {
      const username = metadata[eventFields.resourceUsername].getDataValue(record)
      || metadata[eventFields.resourceEmail].getDataValue(record);

      return `${username ? `The user, ${username},` : 'A user'} was locked out.`;
    },
  },
  [EventTypes.userLoginAttempt]: {
    customIcons: record => ({
      arrow: arrowIcon,
      center: String(record.success) === 'true' ? loginSuccessIcon : loginFailIcon,
      left: userIcon,
      right: portalIcon,
    }),
    service: eventServices.directory,
    summary: record => {
      const username = metadata[eventFields.initiatedByUsername].getDataValue(record)
      || metadata[eventFields.initiatedByEmail].getDataValue(record);

      return `${username ? `The user, ${username},` : 'A user'} ${record.success ? 'was logged' : 'failed to log'} in to User Portal`;
    },
  },
  [EventTypes.userMfaExclusionExpired]: {
    customMiniCardLayout: 1,
    service: eventServices.directory,
    summary: record => `MFA enrollment has expired for user, ${record.resource.username}.`,
  },
  [EventTypes.userPasswordChange]: {
    service: eventServices.systems,
    summary: record => {
      const username = metadata[eventFields.resourceUsername].getDataValue(record)
        || metadata[eventFields.resourceEmail].getDataValue(record);

      return `The password for ${username ? `the user, ${username},` : 'a user'} was changed.`;
    },
  },
  [EventTypes.userPasswordExpired]: {
    service: eventServices.directory,
    summary: record => {
      const username = metadata[eventFields.resourceUsername].getDataValue(record)
      || metadata[eventFields.resourceEmail].getDataValue(record);

      return `The password for ${username ? `the user, ${username},` : 'a user'} expired.`;
    },
  },
  [EventTypes.userPasswordResetRequest]: {
    service: eventServices.directory,
    summary: record => (record.resource?.username ? `The user, ${record.resource.username}, requested to reset their password.` : 'A user requested to reset their password.'),
  },
  [EventTypes.userPasswordSet]: {
    service: eventServices.directory,
    summary: record => {
      const username = metadata[eventFields.resourceUsername].getDataValue(record)
        || metadata[eventFields.resourceEmail].getDataValue(record);

      return `The password for ${username ? `the user, ${username},` : 'a user'} was set.`;
    },
  },
  [EventTypes.userPasswordWarningEmail]: {
    service: eventServices.directory,
    summary: record => (record.resource?.username ? `Password warning email was sent to user, ${record.resource.username}.` : 'Password warning email was sent.'),
  },
  [EventTypes.userSuspended]: {
    service: eventServices.directory,
    summary: record => {
      const username = metadata[eventFields.resourceUsername].getDataValue(record)
      || metadata[eventFields.resourceEmail].getDataValue(record);

      return `The account for ${username ? `the user, ${username},` : 'a user'} was suspended.`;
    },
  },
  [EventTypes.userSuspensionScheduleCreate]: {
    service: eventServices.directory,
    summary: () => 'Suspension scheduled for user',
  },
  [EventTypes.userSuspensionScheduleDelete]: {
    service: eventServices.directory,
    summary: () => 'Scheduled suspension deleted for user',
  },
  [EventTypes.userUnlocked]: {
    service: eventServices.directory,
    summary: record => {
      const username = metadata[eventFields.resourceUsername].getDataValue(record)
      || metadata[eventFields.resourceEmail].getDataValue(record);

      return `${username ? `The user, ${username},` : 'A user'} was unlocked.`;
    },
  },
  [EventTypes.userUnsuspended]: {
    service: eventServices.directory,
    summary: record => {
      const username = metadata[eventFields.resourceUsername].getDataValue(record)
        || metadata[eventFields.resourceEmail].getDataValue(record);

      return `The account for ${username ? `the user, ${username},` : 'a user'} was unsuspended.`;
    },
  },
  [EventTypes.userUpdate]: {
    customIcons: () => ({
      arrow: arrowIcon,
      center: recordUpdate,
      left: userIcon,
      right: portalIcon,
    }),
    service: eventServices.directory,
    summary: record => {
      const username = metadata[eventFields.resourceUsername].getDataValue(record)
      || metadata[eventFields.resourceEmail].getDataValue(record);
      const directObject = username ? `the user, ${username}` : 'a user';
      const numFieldChanges = record.changes.length;

      if (numFieldChanges === 1) {
        const fieldChanged = get(record, 'changes.0.field');

        if (fieldChanged) {
          return `The ${fieldChanged} field was updated on ${directObject}`;
        }
      }

      return `${numFieldChanges} ${numFieldChanges === 1 ? 'field was' : 'fields were'} updated on ${directObject}`;
    },
  },
  [EventTypes.userUpdateProvision]: {
    customMiniCardLayout: 5,
    service: eventServices.directory,
    summary: record => {
      if (!record.application?.name && !record.resource?.username) return 'A user updated provisions.';
      if (!record.application?.name) return `The user, ${record.resource.username}, updated provisions.`;

      // Capitalize application name
      const appName = startCase(record.application.name);

      if (!record.resource?.username) return `A user updated provisions for ${appName}.`;
      return `The user, ${record.resource.username} updated provisions for ${appName}.`;
    },
  },
  [EventTypes.workdayCreate]: {
    service: eventServices.directory,
    summary: record => (record.initiated_by?.email ? `A workday directory was created by user, ${record.initiated_by.email}.` : 'A workday directory was created.'),
  },
  [EventTypes.workdayDelete]: {
    service: eventServices.directory,
    summary: record => (record.initiated_by?.email ? `A workday directory was deleted by user, ${record.initiated_by.email}.` : 'A workday directory was deleted.'),
  },
  [EventTypes.workdayUpdate]: {
    service: eventServices.directory,
    summary: record => (record.initiated_by?.email ? `A workday directory was updated by user, ${record.initiated_by.email}.` : 'A workday directory was updated.'),
  },
  // add error handling so Event Summary doesn't error out when undefined in metadata
  undefined: { },
};

Object.keys(metadata).forEach((fieldName) => {
  const metadatum = metadata[fieldName];

  // Insert name into each metadata object
  metadatum.name = fieldName;

  // If `getDataValue` method does not exist, create it and use path value
  if (!metadatum.getDataValue && metadatum.path) {
    metadatum.getDataValue = d => get(d, metadatum.path);
  }
});

export const formatEventValue = (value) => {
  if (typeof value === 'undefined' || value === null) return dataNotAvailable;
  return value;
};

export default metadata;
